import { Smartphone, GripVertical, Lock, Check, RotateCcw, ArrowUp, ArrowDown } from 'lucide-react'
import { useMobileNavPrefs } from '@/hooks/useMobileNavPrefs'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Settings panel that lets the user pick which 4 nav items appear in the
 * mobile bottom tab bar. The 5th slot is always "More", which opens a sheet
 * containing every unpinned item.
 */
export default function MobileNavSettings() {
  const featureGate = useFeatureGate()
  const {
    pinnedKeys,
    pinnedItems,
    availableItems,
    setPinned,
    togglePin,
    resetToDefault,
    maxPinned,
  } = useMobileNavPrefs()

  const movePinned = (key: string, direction: -1 | 1) => {
    const idx = pinnedKeys.indexOf(key)
    if (idx === -1) return
    const next = [...pinnedKeys]
    const swap = idx + direction
    if (swap < 0 || swap >= next.length) return
    ;[next[idx], next[swap]] = [next[swap], next[idx]]
    setPinned(next)
  }

  const unpinned = availableItems.filter(i => !pinnedKeys.includes(i.key))

  return (
    <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-7 w-7 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center">
            <Smartphone className="h-3.5 w-3.5 text-brand-periwinkle" />
          </div>
          <h3 className="text-base font-semibold">Mobile bottom nav</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5 ml-9">
          Pick up to {maxPinned} shortcuts for the bottom of the app on your phone. Everything else lives behind the <span className="font-medium text-foreground">More</span> tab.
        </p>

        {/* Live preview */}
        <div className="ml-9 mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-2">Preview</p>
          <div className="rounded-2xl border border-border/60 bg-background overflow-hidden">
            <div className="flex items-stretch">
              {pinnedItems.map(item => {
                const Icon = item.icon
                return (
                  <div key={item.key} className="flex-1 flex flex-col items-center justify-center gap-1 py-3 px-1">
                    <div className="flex h-9 w-12 items-center justify-center rounded-xl bg-brand-ice dark:bg-brand-royal/15">
                      <Icon className="h-5 w-5 text-brand-royal" />
                    </div>
                    <span className="text-[10px] font-medium leading-tight text-muted-foreground truncate max-w-full">
                      {item.name}
                    </span>
                  </div>
                )
              })}
              {Array.from({ length: maxPinned - pinnedItems.length }).map((_, i) => (
                <div key={`empty-${i}`} className="flex-1 flex flex-col items-center justify-center gap-1 py-3 px-1 opacity-40">
                  <div className="flex h-9 w-12 items-center justify-center rounded-xl border border-dashed border-border/60" />
                  <span className="text-[10px] text-muted-foreground/60">Empty</span>
                </div>
              ))}
              <div className="flex-1 flex flex-col items-center justify-center gap-1 py-3 px-1 bg-muted/40">
                <div className="flex h-9 w-12 items-center justify-center rounded-xl bg-gradient-brand">
                  <span className="text-white text-base leading-none">···</span>
                </div>
                <span className="text-[10px] font-medium leading-tight text-brand-royal">More</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pinned (reorderable) */}
        <div className="ml-9">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              Pinned · {pinnedItems.length}/{maxPinned}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>
          <div className="space-y-1.5">
            {pinnedItems.length === 0 && (
              <p className="text-xs text-muted-foreground italic px-2 py-3">No items pinned yet — pick some below.</p>
            )}
            {pinnedItems.map((item, idx) => {
              const Icon = item.icon
              const locked = Boolean(item.gatedBy) && !featureGate.isAllowed(item.gatedBy!)
              return (
                <div
                  key={item.key}
                  className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
                  <div className="h-8 w-8 rounded-lg bg-brand-ice dark:bg-brand-royal/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-brand-royal" />
                  </div>
                  <span className="flex-1 text-sm font-medium truncate">{item.name}</span>
                  {locked && (
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Locked
                    </span>
                  )}
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={idx === 0}
                      onClick={() => movePinned(item.key, -1)}
                      aria-label={`Move ${item.name} up`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={idx === pinnedItems.length - 1}
                      onClick={() => movePinned(item.key, 1)}
                      aria-label={`Move ${item.name} down`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePin(item.key)}
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Unpin
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Available */}
        {unpinned.length > 0 && (
          <div className="ml-9 mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-2">
              Available
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {unpinned.map(item => {
                const Icon = item.icon
                const locked = Boolean(item.gatedBy) && !featureGate.isAllowed(item.gatedBy!)
                const full = pinnedItems.length >= maxPinned
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={full}
                    onClick={() => togglePin(item.key)}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2 text-left transition-colors',
                      full ? 'opacity-50 cursor-not-allowed' : 'hover:border-brand-royal/40 hover:bg-brand-ice/30 dark:hover:bg-brand-royal/5'
                    )}
                  >
                    <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <span className="flex-1 text-sm truncate">{item.name}</span>
                    {locked && <Lock className="h-3 w-3 text-muted-foreground/60" />}
                    {!full && <Check className="h-4 w-4 text-muted-foreground/30" />}
                  </button>
                )
              })}
            </div>
            {pinnedItems.length >= maxPinned && (
              <p className="text-[11px] text-muted-foreground mt-2 italic">
                Bottom nav is full — unpin something above to add a different shortcut.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
